/**
 * MXFP4: 4-bit weights split across two tensors, one of which looks like plain bytes.
 *
 * ## The failure this exists to fix
 *
 * `openai/gpt-oss-20b` stores each expert projection as a **pair**:
 *
 *     model.layers.N.mlp.experts.gate_up_proj_blocks   U8  [32, 5760, 90, 16]
 *     model.layers.N.mlp.experts.gate_up_proj_scales   U8  [32, 5760, 90]
 *
 * Both are `U8`, so counting elements gives 8.00 bits per element and the map
 * paints the model's entire bulk mass as an 8-bit checkpoint. It is a 4.25-bit
 * one. The bytes are right and the *denominator* is wrong: each byte of
 * `_blocks` holds two 4-bit values, and the `_scales` plane is not weights at
 * all — it is one shared E8M0 exponent per block.
 *
 * ## What makes the pair checkable rather than assumed
 *
 * The two shapes have to agree in a way that nothing else does: `_scales` is
 * exactly `_blocks` with the last axis dropped, so there is precisely one scale
 * per block. That fixes the block count. The last axis then says how many bytes
 * a block is — 16 — and MXFP4's definition says each is two values, giving 32
 * values per block.
 *
 *     32 x 5760 x 90 blocks  x  16 bytes x 2 values  =  265 420 800 weights
 *     (132 710 400 + 8 294 400) bytes x 8 / that     =  4.25 bits per weight
 *
 * and `_blocks` alone is exactly 4.00. That last equality is the check with
 * teeth: it only lands on a round 4.00 if the block geometry was read right.
 *
 * ## Why the config is required, and why the shapes are not enough
 *
 * Shapes alone cannot tell 4-bit/block-32 from 8-bit/block-16 — the same trap
 * `mlx.ts` documents at length, where one equation constrains only the product.
 * A 16-byte block is equally consistent with both, and picking wrong scales the
 * headline figure by exactly two.
 *
 * So the declaration has to name the format, and here the *name is the whole
 * declaration*: `gpt-oss` states `{"quant_method": "mxfp4"}` and nothing else —
 * no `bits`, no `group_size`. MXFP4 is a specification, and 4-bit elements
 * under one E8M0 per 32 is what the name means. The shapes then verify that
 * reading rather than supply it, and a pair whose geometry says anything else
 * is refused instead of folded.
 *
 * ## Folding only what verifies
 *
 * A `_scales` with no `_blocks` partner, a pair whose axes do not line up, a
 * pair whose block arithmetic does not come out at 32 — none of them fold. That
 * is a property of the construction, not a list of exceptions: a singular
 * `router.scale` never reaches this code, and would fail the pair lookup if it
 * did.
 *
 * ## Membership is the blocks-name prefix, **not** the module path
 *
 * Worth stating because the sibling folders say the opposite and are right to.
 * `bnb-nf4.ts` groups by module path — its sidecars are *children* of `.weight`
 * and the anchor moves three ways across families, so nothing else can find
 * them. That rule does not transfer here, and applying it would be wrong on the
 * models this folder exists for.
 *
 * On a MoE expert module there is no submodule to group by.
 * `model.layers.N.mlp.experts` holds six tensors directly — two projections'
 * worth of pairs and their biases — and there is no `experts.down_proj` node
 * between them:
 *
 *     …mlp.experts.gate_up_proj_blocks   …mlp.experts.gate_up_proj_scales
 *     …mlp.experts.down_proj_blocks      …mlp.experts.down_proj_scales
 *     …mlp.experts.gate_up_proj_bias     …mlp.experts.down_proj_bias
 *
 * The projection identity lives in the **tensor-name prefix**, which is what
 * this code keys on. "Everything under the module path" would merge all six
 * into one row, and the row would be quietly wrong rather than obviously so —
 * on `gpt-oss-20b` layer 0:
 *
 *     per pair, as built   4.250000 bits/weight   codebook share 5.8824%
 *     merged by module     4.254078               codebook share 5.8747%
 *
 * Close enough to read as a rounding artefact, far enough to be a different
 * claim about the checkpoint — and it would put the biases, which are real
 * weights in a real float dtype, inside a quantized row that reports a bits
 * figure they do not have.
 *
 * Pure: shapes in, arithmetic out, no I/O.
 */

import type { DeclaredQuantization } from "./mlx.js";

/** What a block-scaled group needs to describe itself. */
export interface BlockScaledTensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  readonly byteLength: number;
}

export interface BlockScaledGroup {
  /** Logical name, e.g. `model.layers.0.mlp.experts.gate_up_proj`. */
  readonly prefix: string;
  /** The payload tensor — `<prefix>_blocks`. */
  readonly blocksName: string;
  /** Both physical tensors, payload first. */
  readonly partNames: readonly string[];
  readonly bits: number;
  /** Values sharing one exponent — 32 for MXFP4. */
  readonly groupSize: number;
  /** Weights in the logical matrix, *before* nibble packing. */
  readonly params: number;
  /** Payload plus scales. */
  readonly bytes: number;
  /** 4.25 — what the scheme actually costs. */
  readonly effectiveBpw: number;
  /** 4.00 — `_blocks` alone, the nominal width. */
  readonly payloadBpw: number;
}

const BLOCKS_LEAF = "blocks";
const SCALES_LEAF = "scales";

/**
 * The separators a checkpoint may put in front of the `blocks`/`scales` leaf.
 *
 * `gpt-oss` ships the same payload under both spellings: the sharded
 * safetensors write `…gate_up_proj_blocks` and `original/model.safetensors`
 * writes `…mlp1_weight.blocks`. Matching only the underscore meant the
 * `original/` variant — which the variant picker surfaces and the config gate
 * admits — never formed a single pair. Its 48 `.blocks` tensors fell back to
 * `U8` at 8.00 and the model read **11.96B against a card that says 20.9B**,
 * 42.8% light.
 *
 * Nothing about the arithmetic was wrong; the bytes never reached it. That is
 * the second half of one lesson — the first being the GGUF type-39 row, where
 * the geometry was solved correctly and then labelled `type39` because no entry
 * named it. **A name is a lookup key, not evidence**, and both defects were a
 * string that failed to match rather than a number that came out wrong. Plan
 * §24 records the structural answer; this constant is the local one.
 */
const SEPARATORS = ["_", "."] as const;

/**
 * Split a `…{sep}blocks` name into its prefix and the separator it used.
 *
 * The separator is returned rather than discarded because the `scales` partner
 * must be looked up with **the same one**. Accepting `x.blocks` beside
 * `x_scales` would be pairing two tensors that no checkpoint writes together,
 * and while `verifyPair`'s geometry gate would almost certainly refuse the
 * result, "almost certainly" is not the standard for a pair that decides a
 * model's parameter count.
 */
function splitBlocksName(
  name: string,
): { readonly prefix: string; readonly separator: string } | undefined {
  for (const separator of SEPARATORS) {
    const suffix = `${separator}${BLOCKS_LEAF}`;
    if (name.endsWith(suffix)) {
      return { prefix: name.slice(0, -suffix.length), separator };
    }
  }
  return undefined;
}

/**
 * MXFP4, by definition of the name: 4-bit elements, 32 to a block, one shared
 * E8M0 exponent each. Not inferred from shapes — the shapes *check* these.
 */
const MXFP4_BITS = 4;
const MXFP4_BLOCK = 32;

/** Both planes are byte arrays; anything wider is not this layout. */
const BYTE_DTYPES: ReadonlySet<string> = new Set(["U8", "I8"]);

function product(shape: readonly number[]): number {
  return shape.reduce((a, b) => a * b, 1);
}

/** Does the repo declare the one format this module knows how to verify? */
function declaresMxfp4(declared: DeclaredQuantization | undefined): boolean {
  return (declared?.method ?? "").toLowerCase().includes("mxfp4");
}

/**
 * The pair test. Returns the group only when every shape agrees; `undefined`
 * means "not this layout", never "probably this layout".
 */
function verifyPair(
  blocks: BlockScaledTensorLike,
  scales: BlockScaledTensorLike,
): Omit<BlockScaledGroup, "prefix"> | undefined {
  if (!BYTE_DTYPES.has(blocks.dtype) || !BYTE_DTYPES.has(scales.dtype))
    return undefined;
  // A block axis to drop, and something to drop it from.
  if (blocks.shape.length < 2) return undefined;
  if (scales.shape.length !== blocks.shape.length - 1) return undefined;
  // Exactly one scale per block — the constraint that fixes the block count.
  for (let i = 0; i < scales.shape.length; i += 1) {
    if (scales.shape[i] !== blocks.shape[i]) return undefined;
  }

  const bytesPerBlock = blocks.shape[blocks.shape.length - 1] as number;
  if (!Number.isInteger(bytesPerBlock) || bytesPerBlock <= 0) return undefined;
  // The declared format says 4-bit, so a block is twice its bytes. If that does
  // not come out at 32, the pair is some other scheme wearing these suffixes.
  if ((bytesPerBlock * 8) / MXFP4_BITS !== MXFP4_BLOCK) return undefined;

  // Both planes must actually be one byte per element, or the shapes describe
  // something the byte counts contradict.
  if (blocks.byteLength !== product(blocks.shape)) return undefined;
  if (scales.byteLength !== product(scales.shape)) return undefined;

  const params = product(scales.shape) * MXFP4_BLOCK;
  if (params <= 0) return undefined;
  const bytes = blocks.byteLength + scales.byteLength;

  return {
    blocksName: blocks.name,
    partNames: [blocks.name, scales.name],
    bits: MXFP4_BITS,
    groupSize: MXFP4_BLOCK,
    params,
    bytes,
    effectiveBpw: (bytes * 8) / params,
    payloadBpw: (blocks.byteLength * 8) / params,
  };
}

/**
 * Every `blocks`/`scales` pair whose geometry verifies, in header order.
 *
 * The leaf may be separated by `_` or `.` — see `SEPARATORS`; both spellings
 * occur in one repo.
 *
 * Empty unless the repo declares MXFP4 — without that the bits/block split is
 * unknowable from shapes, and an unfolded pair reading 8.00 is at least honest
 * about its bytes where a guessed fold would not be.
 */
export function findBlockScaledGroups(
  tensors: readonly BlockScaledTensorLike[],
  declared?: DeclaredQuantization,
): BlockScaledGroup[] {
  if (!declaresMxfp4(declared)) return [];

  const byName = new Map(tensors.map((t) => [t.name, t]));
  const groups: BlockScaledGroup[] = [];
  for (const blocks of tensors) {
    const split = splitBlocksName(blocks.name);
    if (!split) continue;
    // The same separator on both halves — see `splitBlocksName`.
    const scales = byName.get(`${split.prefix}${split.separator}${SCALES_LEAF}`);
    if (!scales) continue;
    const verified = verifyPair(blocks, scales);
    if (verified) groups.push({ prefix: split.prefix, ...verified });
  }
  return groups;
}
